import { useEffect, useMemo, useState } from 'react';
import styles from './ShareModal.module.scss';
import Modal from '../Modal';
import Button from '../../button/Button';
import type { MenuProps } from 'antd';
import { Dropdown, Tooltip } from 'antd';
import IconInfoFilled from 'src/assets/icons/info-filled.svg?react';
import GreenShareIcon from 'src/assets/icons/share-green.svg?react';
import CloseIcon from '@assets/icons/close.svg?react'
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import { createStudentLink, IconStudent, IconTeacher, timeExpiration } from '@src/constants';
import { AbsentStudentLesson, useGetAbsentStudentLessonLazyQuery } from '@src/api/graphql-codegen';
import { useToast } from '@src/hooks';
import { CopyToClipboard } from '@src/utils/componentCasts';

const STUDENT_DISABLED_TIP = "Student links aren't available for lessons that only contain a survey.";

type Props = {
  open: boolean;
  title: string;
  shareId?: string;
  onClose: () => void;
  continueWithTeacher: () => void;
  continueWithStudent: () => void;
  isSll?: boolean;
  disableStudent?: boolean;
};

function ShareModal(props: Props) {
  const { open, title, shareId, isSll, disableStudent, onClose, continueWithTeacher, continueWithStudent } = props;

  const { successToast } = useToast();

  const [isYourTipHovered, setIsYourTipHovered] = useState(false);
  const [isChildTipHovered, setIsChildTipHovered] = useState(false);
  const [studentLinkActive, setStudentLinkActive] = useState<AbsentStudentLesson | undefined>();

  const [getAbsentStudentLesson, {loading}] = useGetAbsentStudentLessonLazyQuery({ fetchPolicy: 'network-only',
    onCompleted(data) {
      if (data.getAbsentStudentLesson) {
        setStudentLinkActive(data.getAbsentStudentLesson)
      }
    },
  })

  useEffect(() => {
    if (shareId && !disableStudent) {
      getAbsentStudentLesson({
        variables: {
          input: { id: shareId }
        }
      })  
    }
  }, [getAbsentStudentLesson, shareId, disableStudent])

  const copyToClipboard = (e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation()
    } else {
      e.stopPropagation()
    }
    successToast('Link Copied Successfully!');
  }

  const generateStudentLink = (e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation()
    } else {
      e.stopPropagation()
    }
    continueWithStudent()
  }

  const isActive = useMemo(() => {
    if (studentLinkActive) {
      const currentTime = new Date ().getTime ();
      const expireTime = parseInt(studentLinkActive?.expireAt ?? '0')
      return expireTime - currentTime > 0
    }
    return false
  }, [studentLinkActive])

  const items: MenuProps['items'] = [{
      key: '1',
      label: (
        <div className={styles.greyMenuitem} onClick={() => {} }>
          <div className={styles.outlineIcon}>
            <IconInfoFilled />
          </div>
          <div className={styles.activeInfo}>
            <span>
              {timeExpiration(parseInt(studentLinkActive?.expireAt ?? '0'))}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: '2',
      label: (
        <CopyToClipboard text={createStudentLink(studentLinkActive?.link ?? '', isSll)} >
          <div className={styles.generationMenuitem} onClick={() => {} }>
          {'Copy Link'}
          </div>
        </CopyToClipboard>
      ),
      onClick: (e) => copyToClipboard(e)
    },
    {
      key: '3',
      label: (
        <div className={styles.generationMenuitem} onClick={() => {} }>
        {'Regenerate Link'}
        </div>
      ),
      onClick: (e) => generateStudentLink(e)
    }
  ];

  const studentViewStyle = useMemo(() => {
    let classes = [isActive ? styles.activeSelectOptionView : styles.selectOptionView];

    if (isChildTipHovered) {
      classes.push(styles.selectOptionViewHover);
    }
    return classes.join(' ')
  }, [isActive, isChildTipHovered])

  const educatorInfoViewStyle = useMemo(() => {
    let classes = [styles.filledInfoIcon];

    if (isYourTipHovered) {
      classes.push(styles.selectOptionViewHover);
    }

    return classes.join(' ')
  }, [isYourTipHovered])

  const studentInfoViewStyle = useMemo(() => {
    let classes = [styles.filledInfoIcon];

    if (isActive) {
      classes.push(styles.activeBorder);
    }

    if (isChildTipHovered) {
      classes.push(styles.selectOptionViewHover);
    }

    return classes.join(' ')
  }, [isActive, isChildTipHovered])

  return (
    <Modal global={false} open={open} onClose={onClose} closeButton={false} className={styles.wrapper}>
      <div className={styles.mainView}>
        <div className={styles.logo}>{<GreenShareIcon />}</div>
        {onClose && (
          <div className={styles.closeIcon} role='button' onClick={onClose}>
            <CloseIcon />
          </div>
        )}
        <div className={styles.title}>{title}</div>
        <div className={styles.content}>
          <div className={styles.selectView}>
            <div className={styles.teacherHoverView} style={{display: isYourTipHovered ? "block" : "none"}}>
              <div className={styles.hoverTipDetail}>{'Share this lesson with an Educator.'}</div>
            </div>
            <div className={`${styles.selectOptionView} ${isYourTipHovered ? styles.selectOptionViewHover : ''}`}>
              {<div className={styles.iconView}>
                <div className={styles.circleView} onMouseOver={() => setIsYourTipHovered(true)} onMouseLeave={() => setIsYourTipHovered(false)}>
                  <div className={educatorInfoViewStyle}>
                      <IconInfoFilled />
                    </div>
                  </div>
              </div>}
              <Button size="small" disabled={loading} className={styles.selectButton} onClick={() => continueWithTeacher()} label={'Educator'} leftIcon={<LordIcon src={IconTeacher} isHover={true} colors={{primary: "#FFFFFF", secondary: "#FFFFFF"}} size={40} />} />
            </div>
            {<div className={styles.studentHoverView} style={{display: isChildTipHovered ? "block" : "none"}}>
              <div className={styles.hoverTipDetail}>{disableStudent ? STUDENT_DISABLED_TIP : 'Share lesson with a student who missed the class. Please note, this lesson is not designed to be used exclusively for distance-learning.'}</div>
            </div>}
            <div className={studentViewStyle}>
              {<div className={styles.iconView}>
                <div className={styles.circleView} onMouseOver={() => setIsChildTipHovered(true)} onMouseLeave={() => setIsChildTipHovered(false)}>
                  <div className={studentInfoViewStyle}>
                    <IconInfoFilled />
                  </div>
                </div>
              </div>}
              { disableStudent ?
                (<Tooltip title={STUDENT_DISABLED_TIP} placement="bottom">
                  <span>
                    <Button size="small" disabled className={styles.selectButton} label={'Student'} leftIcon={<LordIcon src={IconStudent} isHover={true} colors={{primary: "#FFFFFF", secondary: "#FFFFFF"}} size={40} />}/>
                  </span>
                </Tooltip>) :
                isActive ?
                (<Dropdown menu={{ items }} trigger={["click"]} placement="bottom">
                  <Button size="small" disabled={loading} className={styles.selectButton} label={'Student'} leftIcon={<LordIcon src={IconStudent} isHover={true} colors={{primary: "#FFFFFF", secondary: "#FFFFFF"}} size={40} />}/>
                </Dropdown>) : 
                (<Button size="small" disabled={loading} className={styles.selectButton} label={'Student'} onClick={() => continueWithStudent()} leftIcon={<LordIcon src={IconStudent} isHover={true} colors={{primary: "#FFFFFF", secondary: "#FFFFFF"}} size={40} />}/>)
              }
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default ShareModal;
